import React from 'react';

import { useRelayEnvironment } from 'react-relay';
import { fetchQuery, graphql } from 'relay-runtime';

import AddressEntryPage from '../AddressEntryPage';
import AddressErrorPage from '../AddressErrorPage';
import AddressSearchPage from '../AddressSearchPage';
import AddressSuggestionPage from '../AddressSuggestionPage';
import LoadingPage from '../LoadingPage';
import UnknownErrorPage from '../UnknownErrorPage';
import ShippingAddressPage, { ShippingOptionType } from './ShippingAddressPage';
import { ShippingAddressPageCreateCreditAccountMutation as ShippingAddressPageCreateCreditAccountMutationType } from 'src/clients/Network/gql/build/ShippingAddressPageCreateCreditAccountMutation.graphql';
import { ShippingAddressPageValidateAddressQuery as ShippingAddressPageValidateAddressQueryType } from 'src/clients/Network/gql/build/ShippingAddressPageValidateAddressQuery.graphql';
import { AddressInput, AddressType } from 'src/clients/Network/gql/types/address';
import { trackPageView } from 'src/clients/tracking';
import { NavigationContext } from 'src/components/creditApplication/NavigationProvider';
import { showGlobalErrorAlert, useGlobalStore } from 'src/state/global';
import { AddressTypeToAddressInput } from 'src/utils/address';
import { useAsyncMutation } from 'src/utils/graphql';

const ShippingAddressPageValidateAddressQuery = graphql`
  query ShippingAddressPageValidateAddressQuery($q: AddressValidityRequest!) {
    addressValidity(q: $q) {
      address {
        id
        streetAddress
        primaryAddressLine
        secondaryAddressLine
        locality
        subdivision
        zip
      }
      addressFormatted {
        id
        streetAddress
        primaryAddressLine
        secondaryAddressLine
        locality
        subdivision
        zip
      }
      error
      errorSkippable
    }
  }
`;

// TODO(rock/szhang): add other fields as needed
const ShippingAddressPageCreateCreditAccountMutation = graphql`
  mutation ShippingAddressPageCreateCreditAccountMutation($q: CreateCreditAccountRequest!) {
    createCreditAccount(q: $q) {
      data {
        customer {
          id
        }
      }
      errors
    }
  }
`;
type ShippingAddressPageType =
  | 'SHIPPING_ADDRESS'
  | 'SEARCH_ADDRESS'
  | 'SUGGESTED_ADDRESS'
  | 'MANUAL_ADDRESS'
  | 'ADDRESS_VALIDATION_ERROR';

const ShippingAddressRouter = () => {
  const relayEnv = useRelayEnvironment();
  const { nextPage } = React.useContext(NavigationContext);
  const shippingAddress = useGlobalStore((state) => state.personalInformation.shippingAddress);
  const creditApplicationId = useGlobalStore((state) => state.creditApplicationId);

  const [commitCreateCreditAccount, createCreditAccountLoading] =
    useAsyncMutation<ShippingAddressPageCreateCreditAccountMutationType>(
      ShippingAddressPageCreateCreditAccountMutation,
    );
  const [currentPage, setCurrentPage] = React.useState<ShippingAddressPageType>('SHIPPING_ADDRESS');
  const [enteredAddress, setEnteredAddress] = React.useState<AddressInput>();
  const [suggestedAddress, setSuggestedAddress] = React.useState<AddressType>();
  const [shipmentOption, setShipmentOption] = React.useState<ShippingOptionType>();
  const [signatureRequired, setSignatureRequired] = React.useState<boolean>(false);
  const [addressValidationError, setAddressValidationError] = React.useState<string>();
  const [addressValidationSkippable, setAddressValidationSkippable] = React.useState<boolean>();
  const [validateLoading, setValidateLoading] = React.useState(false);
  const [continueLoading, setContinueLoading] = React.useState(false);

  // can be original address, entered address, or suggested address
  const [addressToSubmit, setAddressToSubmit] = React.useState<AddressInput>();

  React.useEffect(() => {
    switch (currentPage) {
      case 'SUGGESTED_ADDRESS':
        trackPageView('ShippingAddressSuggested');
        break;
      case 'ADDRESS_VALIDATION_ERROR':
        trackPageView('ShippingAddressValidationError');
        break;
      case 'SHIPPING_ADDRESS':
        trackPageView('ShippingAddressShipmentOptions');
        break;
      case 'SEARCH_ADDRESS':
        trackPageView('ShippingAddressSearch');
        break;
      case 'MANUAL_ADDRESS':
        trackPageView('ShippingAddressManual');
        break;
      default:
        break;
    }
  }, [currentPage]);

  const onSelectAddress = React.useCallback((address: AddressInput) => {
    setEnteredAddress(address);
    setCurrentPage('MANUAL_ADDRESS');
  }, []);

  const onEditShippingAddress = React.useCallback(() => {
    setCurrentPage('SEARCH_ADDRESS');
  }, []);

  const createCreditAccount = React.useCallback(async () => {
    if (shippingAddress == null || creditApplicationId == null) {
      showGlobalErrorAlert();
      return;
    }

    try {
      useGlobalStore.setState((s) => ({
        ...s,
        shippingMethod: shipmentOption,
      }));
      const res = await commitCreateCreditAccount({
        variables: {
          q: {
            shippingAddress: addressToSubmit ?? shippingAddress,
            creditApplicationId,
            signatureRequired,
            deliveryMethod: shipmentOption?.deliveryMethod,
          },
        },
      });
      if (res.createCreditAccount.errors.length > 0) {
        showGlobalErrorAlert();
      }
    } catch (error) {
      showGlobalErrorAlert();
    }
  }, [
    commitCreateCreditAccount,
    creditApplicationId,
    signatureRequired,
    shipmentOption?.deliveryMethod,
    shippingAddress,
    addressToSubmit,
  ]);

  const onContinue = React.useCallback(
    async (address?: AddressInput) => {
      setContinueLoading(true);
      if (address == null) {
        showGlobalErrorAlert();
      } else {
        useGlobalStore.setState((info) => ({
          ...info,
          personalInformation: {
            ...info.personalInformation,
            shippingAddress: address,
          },
        }));
        await createCreditAccount();
        await nextPage();
      }
      setContinueLoading(false);
    },
    [nextPage, createCreditAccount],
  );

  const onValidateAddress = React.useCallback(
    async (address: AddressInput) => {
      setValidateLoading(true);
      setEnteredAddress(address);

      try {
        const response = await fetchQuery<ShippingAddressPageValidateAddressQueryType>(
          relayEnv,
          ShippingAddressPageValidateAddressQuery,
          {
            q: {
              primaryAddressLine: address?.primaryAddressLine ?? '',
              secondaryAddressLine: address?.secondaryAddressLine ?? '',
              locality: address?.locality ?? '',
              subdivision: address?.subdivision ?? '',
              zip5: address?.zip5 ?? '',
              purpose: 'SHIPPING',
            },
          },
          { fetchPolicy: 'network-only' },
        ).toPromise();

        if (response?.addressValidity?.error != null) {
          setAddressValidationError(response?.addressValidity?.error);
          setAddressValidationSkippable(response?.addressValidity?.errorSkippable);
          setCurrentPage('ADDRESS_VALIDATION_ERROR');
          setValidateLoading(false);
        } else if (response?.addressValidity?.addressFormatted != null) {
          setSuggestedAddress(response.addressValidity.addressFormatted);
          setCurrentPage('SUGGESTED_ADDRESS');
          setValidateLoading(false);
        } else {
          onContinue(address);
          setValidateLoading(false);
        }
      } catch (error) {
        setCurrentPage('ADDRESS_VALIDATION_ERROR');
      } finally {
        setValidateLoading(false);
      }
    },
    [relayEnv, setCurrentPage],
  );

  if (createCreditAccountLoading || continueLoading) {
    return <LoadingPage loadingText='Just a moment' />;
  } else if (currentPage === 'SHIPPING_ADDRESS' && shippingAddress != null) {
    return (
      <ShippingAddressPage
        address={addressToSubmit ?? shippingAddress}
        signatureRequired={signatureRequired}
        shipmentOption={shipmentOption}
        onChangeShipmentOption={setShipmentOption}
        onEditShippingAddress={onEditShippingAddress}
        onChangeSignatureRequired={setSignatureRequired}
        onContinue={() => onContinue(addressToSubmit ?? shippingAddress)}
        continueLoading={continueLoading || createCreditAccountLoading}
      />
    );
  } else if (currentPage === 'SEARCH_ADDRESS') {
    return (
      <AddressSearchPage
        addressType='shipping'
        onEnterManually={() => setCurrentPage('MANUAL_ADDRESS')}
        onContinue={onSelectAddress}
      />
    );
  } else if (currentPage === 'MANUAL_ADDRESS') {
    return (
      <AddressEntryPage onContinue={onValidateAddress} loading={validateLoading} prefillAddress={enteredAddress} />
    );
  } else if (currentPage === 'SUGGESTED_ADDRESS' && suggestedAddress != null) {
    return (
      <AddressSuggestionPage
        suggestedAddress={suggestedAddress}
        onAccept={() => {
          setAddressToSubmit(AddressTypeToAddressInput(suggestedAddress));
          setCurrentPage('SHIPPING_ADDRESS');
        }}
        onReject={() => {
          setAddressToSubmit(enteredAddress);
          setCurrentPage('SHIPPING_ADDRESS');
        }}
        loading={continueLoading}
      />
    );
  } else if (currentPage === 'ADDRESS_VALIDATION_ERROR') {
    return (
      <AddressErrorPage
        error={addressValidationError}
        skippable={addressValidationSkippable}
        purpose='shipping'
        enteredAddress={enteredAddress}
        onEditAddress={() => setCurrentPage('SEARCH_ADDRESS')}
        onContinue={() => {
          setAddressToSubmit(enteredAddress ?? shippingAddress);
          setCurrentPage('SHIPPING_ADDRESS');
        }}
        loading={continueLoading}
      />
    );
  } else {
    return <UnknownErrorPage />;
  }
};

export default ShippingAddressRouter;
